import { createMutation } from "react-query-kit";
import type { MutationFunction } from "@tanstack/react-query";

import { axiosClient, queryClient } from "@api/clients.ts";
import type { IPatchApiEntities } from "@api/apiPath";
import { getApi } from "@api/apiPath";

export const createPatchFunc = <
  TResponse,
  TPathParams extends object | undefined,
  TData extends object | undefined,
>(
  path: IPatchApiEntities,
) => {
  type IVariables = TData extends undefined
    ? {
        pathParam: TPathParams;
      }
    : {
        pathParam: TPathParams;
      } & TData;

  const { key, getPath } = getApi(`${path}.patch`);

  const patchFunc: MutationFunction<TResponse, IVariables> = ({
    pathParam,
    ...restParam
  }) => {
    return axiosClient.patch(getPath(pathParam as never), restParam);
  };

  return { patchFunc, key };
};

export const createPatchHook = <TResponse, TVariables>(
  patchFunc: MutationFunction<TResponse, TVariables>,
  path: IPatchApiEntities,
) => {
  const usePatch = createMutation<TResponse, TVariables>({
    mutationFn: patchFunc,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [`${path}.getAll`],
      });
      queryClient.invalidateQueries({
        queryKey: [`infinite.${path}.getAll`],
      });

      if (
        variables &&
        typeof variables === "object" &&
        "pathParam" in variables
      ) {
        queryClient.invalidateQueries({
          queryKey: [`${path}.get`, { pathParam: variables.pathParam }],
        });
      } else {
        queryClient.invalidateQueries({ queryKey: [`${path}.get`] });
      }
    },
  });

  return { usePatch };
};

export const createPatchApi = <
  TResponse,
  TPathParams extends object | undefined,
  TData extends object | undefined,
>(
  path: IPatchApiEntities,
) => {
  const { patchFunc } = createPatchFunc<TResponse, TPathParams, Partial<TData>>(
    path,
  );
  const { usePatch } = createPatchHook(patchFunc, path);

  return { patchFunc, usePatch };
};
